// react
import { MouseEvent, useContext, useState } from 'react';

// types
import { ActionButton, ExpenseType, Feedback } from '../../../@types';

// i18n
import { Trans, useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../App.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, AlertTitle, Grid, Typography } from '@mui/material';

// dialogs
import { DialogActions, DialogActionsProps } from '../DialogActions/DialogActions';

// ui
import { Loading } from '../../ui/Loading/Loading';

// props
export interface DialogUpdateExpenseTypeStatusProps {
	isOpen: boolean;
	onClose: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
	onSuccess?: () => void;
}

export const DialogUpdateExpenseTypeStatus = ({
	isOpen,
	onClose,
	onSuccess,
}: DialogUpdateExpenseTypeStatusProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialogExpenseType = searchParams.get('dialogExpenseType');
	const paramDialogStatus = searchParams.get('dialogStatus');

	// context
	const { setBanner } = useContext(ContextBanner);

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [expenseType, setExpenseType] = useState<ExpenseType | null>(null);

	// fetch expense type
	const { isLoading: isLoadingExpenseType } = useFetch({
		isEnabled: Boolean(paramDialogExpenseType && isOpen),
		url: `${process.env.REACT_APP_API_URL}/expense-types/${paramDialogExpenseType}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setExpenseType(res.data[0]);
			}
		},
	});

	// vars
	const dialogActionProps: Partial<DialogActionsProps> = {};
	const isActivating = paramDialogStatus === 'ACTIVE';

	// deactivate translations
	const dialog = {
		alert: t('alert.deactivateExpenseType'),
		btnSubmit: {
			children: t('deactivateExpenseType'),
			color: 'error',
		},
		i18nKey: 'dialogUpdateExpenseTypeStatus.deactivate.content',
		title: t('deactivateExpenseType'),
	};

	// activate translations
	if (isActivating) {
		dialog.btnSubmit = {
			children: t('activateExpenseType'),
			color: 'primary',
		};
		dialog.alert = t('alert.activateExpenseType');
		dialog.i18nKey = 'dialogUpdateExpenseTypeStatus.activate.content';
		dialog.title = t('activateExpenseType');
	}

	if (expenseType) {
		dialogActionProps.subtitle = {
			children: expenseType.name,
		};
	}

	// patch expenseType
	const { fetchRequest: updateExpenseType, isLoading: isLoadingUpdateExpenseType } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/expense-types/${paramDialogExpenseType}`,
		options: {
			method: 'PATCH',
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
			body: JSON.stringify({
				status: paramDialogStatus,
			}),
		},
		onError: (feedback) => {
			const feedbackNew = {
				alert: {
					children: t('error.default'),
				},
			};

			if (feedback.id === 'EXPENSE_TYPE_UNAUTHORIZED_PATCH') {
				feedbackNew.alert.children = t('feedback.unauthorized');
			}

			setFeedback(feedbackNew);
		},
		onSuccess: () => {
			let message = t('alert.expenseTypeDeactivated', { name: expenseType?.name });

			if (isActivating) {
				message = t('alert.expenseTypeActivated', { name: expenseType?.name });
			}

			// set banner
			setBanner({
				hasClose: true,
				message,
				severity: 'success',
			});

			if (onSuccess) {
				onSuccess();
			}

			// close dialog
			if (onClose) {
				onClose();
			}
		},
	});

	// handlers
	const handleSubmit = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear feedback
		setFeedback(null);

		// update expenseType
		updateExpenseType();
	};

	const handleClose = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear feedback
		setFeedback(null);

		if (onClose) {
			onClose(e);
		}
	};

	const renderContent = () => {
		if (isLoadingExpenseType) {
			return (
				<Grid item xs={12}>
					<Grid alignItems="center" container justifyContent="center">
						<Grid item>
							<Loading />
						</Grid>
					</Grid>
				</Grid>
			);
		}

		return (
			<>
				<Grid item xs={12}>
					<Trans
						components={{
							strong: <Typography color="brand.blue.600" component="span" fontWeight={600} />,
						}}
						i18nKey={dialog.i18nKey}
						values={{ name: expenseType?.name }}
					/>
				</Grid>
				{dialog.alert && (
					<Grid item xs={12}>
						<Alert severity="warning">{dialog.alert}</Alert>
					</Grid>
				)}
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</>
		);
	};

	return (
		<DialogActions
			actions={[
				{
					children: t('cancel'),
					onClick: handleClose,
					variant: 'text',
				},
				{
					...(dialog.btnSubmit as ActionButton),
					loading: isLoadingUpdateExpenseType,
					onClick: handleSubmit,
					variant: 'contained',
				},
			]}
			alignItems="center"
			IconCircleProps={{
				type: 'expenseType',
			}}
			isOpen={isOpen}
			maxWidth={400}
			onClose={handleClose}
			title={{
				children: dialog.title,
			}}
			{...dialogActionProps}>
			<Grid container spacing={2}>
				{renderContent()}
			</Grid>
		</DialogActions>
	);
};
